// Forma neutral de una promoción vigente para la píldora y la pestaña del cajón. Cada pantalla
// traduce su fuente a esta forma; el componente no sabe de crédito ni de promos de venta.
import { formatDateMx } from '@/lib/dateHelpers';

/** Origen de la promo: venta (descuento en caja) o crédito (palancas del plan). */
export type PromotionKind = 'SALE' | 'CREDIT';

export const PROMOTION_KIND_LABEL: Record<PromotionKind, string> = {
    SALE: 'Venta',
    CREDIT: 'Crédito',
};

/** Un dato de la promo: el texto, el motivo de por qué falta, o los dos si llegó a medias. */
export interface PromotionField {
    text?: string;
    /** Por qué la fuente no lo trae; se pinta como «sin fuente» con el motivo en el tooltip. */
    missing?: string;
}

/** Lo que basta para la píldora: nombre y beneficio legible. */
export interface PromotionSummary {
    id: string;
    name: string;
    /** Cada palanca en una frase («$500 off equipo», «Bono de $300 a tu cuenta»). */
    benefits: readonly string[];
    /** Ausente = la pantalla tiene un solo tipo y no hace falta rotularlo. */
    kind?: PromotionKind;
}

/** Una tarjeta de la pestaña «Promociones». */
export interface PromotionView extends PromotionSummary {
    validity: PromotionField;
    audience: PromotionField;
    /** Ausente = la fuente no maneja alcance y la fila no se dibuja. */
    scope?: PromotionField;
}

/** Contenido de la pestaña: cargando, sin fuente o la lista (vacía = de verdad no hay). */
export type PromotionsTabState =
    | { kind: 'loading' }
    | { kind: 'noSource'; reason: string }
    | {
          kind: 'ready';
          promotions: readonly PromotionView[];
          /** Una de las fuentes no respondió: se pinta lo que llegó y se dice qué falta. */
          unavailableNote?: string;
      };

export const NO_BENEFIT_TEXT = 'Sin beneficio legible';

/** El beneficio en una línea; las palancas se unen con «+» como en Promos venta. */
export function benefitText(promotion: PromotionSummary): string {
    return promotion.benefits.length === 0 ? NO_BENEFIT_TEXT : promotion.benefits.join(' + ');
}

/** Orden por defecto: alfabético por nombre. */
export function sortPromotions<T extends PromotionSummary>(promotions: readonly T[]): T[] {
    return [...promotions].sort((a, b) => a.name.localeCompare(b.name, 'es-MX'));
}

/** Texto de la píldora: el nombre si es una, «N promos» si son varias. */
export function promotionPillLabel(promotions: readonly PromotionSummary[]): string {
    if (promotions.length === 1) return promotions[0]?.name ?? '';
    return `${String(promotions.length)} promos`;
}

/** Nombre con su tipo entre paréntesis cuando la pantalla mezcla tipos. */
function nameWithKind(promotion: PromotionSummary): string {
    return promotion.kind === undefined
        ? promotion.name
        : `${promotion.name} (${PROMOTION_KIND_LABEL[promotion.kind]})`;
}

/** Tooltip de la píldora: una línea por promo con su tipo y beneficio. */
export function promotionPillTitle(promotions: readonly PromotionSummary[]): string {
    return sortPromotions(promotions)
        .map((promotion) => `${nameWithKind(promotion)}: ${benefitText(promotion)}`)
        .join('\n');
}

/** El tipo común de todas las promos; `undefined` si no traen tipo o vienen mezcladas. */
export function sharedPromotionKind(
    promotions: readonly PromotionSummary[],
): PromotionKind | undefined {
    const kinds = new Set(promotions.map((promotion) => promotion.kind));
    const [only] = kinds;
    return kinds.size === 1 ? only : undefined;
}

/** Vigencia en `DD/MMM/YYYY`: sin inicio arranca al publicarse; sin fin es permanente. */
export function formatPromotionValidity(
    validFrom: string | null,
    validUntil: string | null,
): string {
    if (validFrom === null && validUntil === null) return 'Permanente';
    if (validFrom === null) return `Hasta ${formatDateMx(validUntil ?? '')}`;
    const from = formatDateMx(validFrom);
    if (validUntil === null) return `Desde ${from} · Permanente`;
    return `${from} → ${formatDateMx(validUntil)}`;
}

/** Etiqueta de la pestaña con su contador en el texto; sin fuente no se cuenta. */
export function promotionsTabLabel(state: PromotionsTabState): string {
    return state.kind === 'ready'
        ? `Promociones ${String(state.promotions.length)}`
        : 'Promociones';
}
